import { useMemo, useState } from 'react'
import { Button, Stack, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { SideContent } from 'pages/Contacts/List/Card/PreviewStyles'
import { toast } from 'components/global/CUK/Toast/Toast'
import { useSites } from 'store'

import { useSelection } from '../store'
import { useDestroy } from './use-actions'
import FormAlarm from './AlarmForm'

const RemoveAction: React.FC<{ onError: (v: string | null) => void }> = ({ onError = () => { } }) => {
  const { t } = useTranslation()
  const [selection, setSelection] = useSelection()
  const [destroy, { loading }] = useDestroy()
  const [{ selected }] = useSites()

  const handleClick = async () => {
    onError(null)
    const roomNumbers = selection.filter(({ alarm }) => Boolean(alarm)).map(({ room }) => room)
    if (roomNumbers.length === 0) {
      toast.success(t('voip.sip.alarm.form.destroy_success', { count: 0 }))
      setSelection([])
      return
    }
    const { error } = await destroy({
      site_sf_id: selected[0].sf_id as string,
      room_numbers: roomNumbers
    })
    if (error) {
      toast.error(t('voip.sip.alarm.form.error'))
      onError(error as string)
    } else {
      toast.success(t('voip.sip.alarm.form.destroy_success', { count: selection.length }))
      setSelection([])
    }
  }
  return (
    <Button
      size={'small'}
      color={'error'}
      variant={'outlined'}
      onClick={handleClick}
      sx={{
        borderColor: 'error.main',
        color: 'error.main',
        '&:hover': {
          backgroundColor: 'error.main',
          color: 'white'
        }
      }}
    >
      <i className={`fa-solid fa-${!loading ? 'trash' : 'spinner fa-spin'}`} style={{ marginRight: 8 }} />
      {
        loading ? t('ui_elements.in_progress') : t('ui_elements.remove')
      }
    </Button>
  )
}

const Header = () => {
  const [selection, setSelection] = useSelection()
  const { t } = useTranslation()
  const theme = useTheme()
  return (
    <Stack justifyContent={'space-between'} direction={'row'} width={'100%'}>
      <Stack direction={'row'} gap={1}>
        <Stack
          sx={{
            width: 32,
            height: 32,
            borderRadius: 16,
            backgroundColor: '#DBE8FE',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <i className={'fa-solid fa-list-check'} style={{ color: theme.palette.primary.main }} />
        </Stack>
        <Stack>
          <Typography component={'span'} sx={{ fontSize: 10 }}>
            {t('voip.sip.room_card_in_mass.count', { count: selection.length })}
          </Typography>
          <Typography component={'span'} sx={{ fontSize: 14, fontWeight: 600 }}>
            {t('voip.sip.room_card_in_mass.title')}
          </Typography>
        </Stack>
      </Stack>
      <i
        className={'fa-light fa-xmark'}
        style={{ cursor: 'pointer' }}
        onClick={() => {
          setSelection([])
        }}
      />
    </Stack>
  )
}

const CardInMass = () => {
  const [openForm, setOpenForm] = useState(false)
  const [selection, setSelection] = useSelection()
  const hasRemoveAction = useMemo(() => {
    return selection.some(({ alarm }) => Boolean(alarm) && alarm?.status !== 'expired')
  }, [selection])
  const [error, setError] = useState<string | null>(null)
  const { t } = useTranslation()
  const theme = useTheme()
  return (
    <SideContent style={{ width: 420, minHeight: 0 }}>
      {
        openForm && (
          <FormAlarm
            room_numbers={selection.map(({ room }) => room)}
            onSubmit={() => {
              setOpenForm(false)
              setSelection([])
            }}
            onClose={() => {
              setOpenForm(false)
            }}
          />
        )}
      <Stack
        gap={3}
        flex={1}
        justifyContent="space-between"
        sx={{
          minHeight: 0,
          height: '100%',
          border: '1px solid #EFEFEF',
          borderRadius: '24px',
          backgroundColor: 'white',
          boxSizing: 'border-box',
          overflow: 'hidden',
          p: 3
        }}
      >
        <Stack gap={2} sx={{ flex: 1, overflow: 'hidden' }}>
          <Header />
          {
            Boolean(error) && (
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'error.main' }}>
                {t(`api_errors.${error}`)}
              </Typography>
            )
          }
          <Stack
            gap={1}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none'
              }
            }}
          >
            {
              selection.map(sip => (
                <Stack key={sip.room} direction={'row'} justifyContent={'space-between'}>
                  <Typography component={'span'} sx={{ color: 'content.secondary', fontSize: 12 }}>
                    {sip.room}
                  </Typography>
                  <Stack
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: theme.palette.grey[200],
                      borderRadius: 10
                    }}
                    justifyContent={'center'}
                    alignItems={'center'}>
                    <i
                      className={'fa-solid fa-minus'}
                      style={{ cursor: 'pointer', fontSize: 12 }}
                      onClick={() => {
                        setSelection(prev => prev.filter(({ room }) => room !== sip.room))
                      }}
                    />
                  </Stack>
                </Stack>
              ))
            }
          </Stack>
        </Stack>
        <Stack direction={'row'} justifyContent={'space-between'} flexShrink={0}>
          <Button
            size={'small'}
            variant={'outlined'}
            onClick={() => {
              setSelection([])
            }}
          >
            {t('ui_elements.cancel')}
          </Button>
          <Stack direction={'row'} gap={1}>
            {
              hasRemoveAction && (
                <RemoveAction onError={(e) => setError(e)} />
              )
            }
            <Button size={'small'} variant={'contained'} color={'primary'} onClick={() => {
              setOpenForm(true)
            }}>
              {
                hasRemoveAction ? t('ui_elements.update') : t('ui_elements.create')
              }
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </SideContent>
  )
}

export default CardInMass